// ✅ Phase 2: Centralized Axios instance with auth token injection.
// All API calls should import from here instead of directly from 'axios'.
import axios from 'axios';

const axiosInstance = axios.create();

// Needed so the httpOnly `refreshToken` cookie (48h) is sent to the API and
// accepted back from it.
axiosInstance.defaults.withCredentials = true;

const getApiUrl = () => process.env.REACT_APP_API || 'http://localhost:8080';

const getStoredAuthKey = () => {
  // Admin token takes precedence, matching the request interceptor below.
  try {
    const raw = localStorage.getItem('adminaccessToken');
    if (raw && JSON.parse(raw)?.token) return 'adminaccessToken';
  } catch (e) {}
  return 'accessToken';
};

// Attach the Bearer token from localStorage to every request
axiosInstance.interceptors.request.use(
  (config) => {
    let token = null;

    // Try user token first
    try {
      const raw = localStorage.getItem("accessToken");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token) token = parsed.token;
      }
    } catch (e) {}

    // Try admin token (overrides user token if present)
    try {
      const raw = localStorage.getItem("adminaccessToken");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.token) token = parsed.token;
      }
    } catch (e) {}

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config._retried) {
      console.log('[refresh-debug] request interceptor on retry, final Authorization:', config.headers.Authorization, config.url);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Silently renew the access token (via the 48h refresh-token cookie) when a
// request comes back 401, then replay it. Concurrent 401s share one refresh.
let isRefreshing = false;
let pendingRequests = [];

const flushPendingRequests = (error, token) => {
  pendingRequests.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingRequests = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config: originalRequest, response } = error;
    const isAuthRoute =
      originalRequest?.url?.includes('/login') ||
      originalRequest?.url?.includes('/adminlogin') ||
      originalRequest?.url?.includes('/refresh-token');

    if (response?.status !== 401 || !originalRequest || originalRequest._retried || isAuthRoute) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject });
      }).then((token) => {
        originalRequest._retried = true;
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retried = true;
    isRefreshing = true;

    try {
      const key = getStoredAuthKey();
      console.log('[refresh-debug] storage key:', key, 'stored before:', localStorage.getItem(key));
      const { data } = await axios.post(
        `${getApiUrl()}/refresh-token`,
        {},
        { withCredentials: true }
      );
      console.log('[refresh-debug] refresh response token:', data.token);

      const stored = JSON.parse(localStorage.getItem(key) || '{}');
      localStorage.setItem(key, JSON.stringify({ ...stored, token: data.token }));
      console.log('[refresh-debug] stored after:', localStorage.getItem(key));

      flushPendingRequests(null, data.token);
      originalRequest.headers.Authorization = `Bearer ${data.token}`;
      console.log('[refresh-debug] retry Authorization header:', originalRequest.headers.Authorization, 'about to retry', originalRequest.url);
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      flushPendingRequests(refreshError, null);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('adminaccessToken');
      window.location.href = '/';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default axiosInstance;
